import { Crown, Map, Network, BarChart3, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { paymentsApi } from "../services/payments";
import { saveUpgradeIntent } from "../lib/sessionRestore";

/**
 * PremiumGateModal — shown when a Standard-tier subscriber (or anonymous
 * authenticated user without an active sub) tries to enter Analyst Mode.
 *
 * The "ask anything in plain English" promise on Standard is unmetered, so
 * the *real* upgrade lever is **mode + capability**: Map, Graph, Dashboard,
 * Scenario Lab, multi-LA reports. This modal makes the value swap explicit
 * and routes the user directly into the Pricing page with intent set to
 * `analyst_toggle` so we can track funnel conversion.
 */
export default function PremiumGateModal({ open, onClose, reason = "analyst" }) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    saveUpgradeIntent({
      returnPath: typeof window !== "undefined" ? window.location.pathname : "/",
      featureName: "analyst_toggle",
    });
    paymentsApi.trackIntercept("analyst_toggle", { reason });
    navigate("/pricing?intent=upgrade&from=analyst_toggle&plan=PREMIUM");
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-md bg-white border-slate-200"
        data-testid="premium-gate-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-5 w-5 text-[#E55B13]" />
            <span className="text-xs font-mono uppercase tracking-wider text-[#E55B13] font-semibold">
              Premium feature
            </span>
          </div>
          <DialogTitle className="text-2xl font-bold text-[#061426] font-['Manrope'] leading-tight">
            Analyst Mode is unlocked on Premium.
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 pt-1">
            Your AI queries stay unlimited on Standard — the upgrade is about
            *how* you see the data. Premium adds the full analyst workspace
            and the reports that come with it.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-2.5">
          {[
            { icon: Map,       label: "Live Mapbox layers — FDDI, CEI, DDCI, deprivation overlays" },
            { icon: Network,   label: "Cytoscape graph of Region → CA → LA → Postcode topology" },
            { icon: BarChart3, label: "Dashboard, Scenario Lab, multi-LA + CA-aggregate reports" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-start gap-2.5 text-sm text-[#061426]">
              <Icon className="h-4 w-4 mt-0.5 text-[#E55B13] shrink-0" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">
            From
          </div>
          <div className="text-xl font-bold text-[#061426] font-['Manrope'] mt-0.5">
            £1,000 <span className="text-sm font-normal text-slate-500">/ LA / month inc. VAT</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Annual billing saves you ~17%. PSF15 auto-discount for .gov.uk emails.
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-600 hover:text-[#061426]"
            data-testid="premium-gate-cancel"
          >
            Maybe later
          </Button>
          <Button
            onClick={handleUpgrade}
            className="bg-[#E55B13] hover:bg-[#c44e10] text-white font-semibold"
            data-testid="premium-gate-upgrade"
          >
            See Premium plan
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
